<template>
  <div v-if="type === 'grid'" class="card mt-4">
    <hr>
    <h5 class="card-title mt-4">Configuração da grade</h5>
    <div class="card-body">
      <div class="row mb-4">
        <div class="col-md-8">
          <label class="form-label">Modelo pronto</label>
          <select class="form-select" v-model="selectedPreset">
            <option value="">--Selecione um modelo--</option>
            <option v-for="(preset, key) in gridPresets" :key="key" :value="key">{{ presetLabel(preset) }}</option>
          </select>
          <small v-if="selectedPresetDescription" class="text-muted d-block mt-2">{{ selectedPresetDescription }}</small>
        </div>
        <div class="col-md-4 d-flex align-items-end">
          <button type="button" class="btn btn-light-primary w-100" @click="applyPreset" :disabled="!selectedPreset">Aplicar modelo</button>
        </div>
      </div>

      <div class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="form-label mb-0">Colunas</label>
          <button type="button" class="btn btn-sm btn-light-primary" @click="addGridColumn">Adicionar coluna</button>
        </div>
        <div class="row mb-2" v-for="(column, index) in options.columns" :key="`column-${index}`">
          <div class="col-md-5">
            <input class="form-control form-control-solid" placeholder="Código" v-model="column.key" @input="emitOptions">
          </div>
          <div class="col-md-5">
            <input class="form-control form-control-solid" placeholder="Nome exibido" v-model="column.label" @input="emitOptions">
          </div>
          <div class="col-md-2">
            <button type="button" class="btn btn-sm btn-light-danger w-100" @click="removeGridColumn(index)">Remover</button>
          </div>
        </div>
      </div>

      <div>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="form-label mb-0">Linhas</label>
          <button type="button" class="btn btn-sm btn-light-primary" @click="addGridRow">Adicionar linha</button>
        </div>
        <div class="grid-rows-list">
          <template v-for="(row, index) in options.rows" :key="`row-${index}`">
            <hr v-if="index > 0" class="grid-row-divider">
            <div class="grid-row-block">
              <div class="grid-row-block__header">
                <span class="grid-row-block__title">Linha {{ index + 1 }}</span>
              </div>
              <div class="row mb-2">
                <div class="col-md-3">
                  <input class="form-control form-control-solid" placeholder="Código" v-model="row.key" @input="emitOptions">
                </div>
                <div class="col-md-4">
                  <input class="form-control form-control-solid" placeholder="Nome exibido" v-model="row.label" @input="emitOptions">
                </div>
                <div class="col-md-3">
                  <select class="form-select form-control-solid" v-model="row.cell_type" @change="emitOptions">
                    <option value="text">Texto livre</option>
                    <option value="autocomplete">Lista com sugestões</option>
                    <option value="autocomplete_strict">Somente valores da lista</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <button type="button" class="btn btn-sm btn-light-danger w-100" @click="removeGridRow(index)">Remover</button>
                </div>
              </div>
              <div class="row mb-2" v-if="row.cell_type !== 'text'">
                <div class="col-md-12">
                  <label class="form-label">Sugestões</label>
                  <DelimiterTagInput
                      :model-value="row.suggestions ?? []"
                      @update:modelValue="updateRowSuggestions(row, $event)"
                  />
                  <small class="text-muted">Separe com vírgula, ponto e vírgula ou Enter.</small>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6" v-for="column in options.columns" :key="`default-${index}-${column.key}`">
                  <label class="form-label">Valor padrão {{ column.label || column.key }}</label>
                  <input class="form-control form-control-solid"
                         :value="options.defaults?.[row.key]?.[column.key] ?? ''"
                         @input="updateDefaultValue(row.key, column.key, $event.target.value)">
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="type === 'image_annotation'" class="card mt-4">
    <hr>
    <h5 class="card-title mt-4">Configuração de imagem</h5>
    <div class="card-body">
      <div class="row">
        <div class="col-md-6">
          <label class="form-label">Origem da imagem</label>
          <select class="form-select form-control-solid" v-model="options.background_source" @change="onBackgroundSourceChange">
            <option value="catalog">Catálogo</option>
            <option value="file">Arquivo</option>
          </select>
        </div>
        <div class="col-md-6" v-if="options.background_source === 'catalog'">
          <label class="form-label">Imagem</label>
          <select class="form-select form-control-solid" v-model="options.image_asset" @change="emitOptions">
            <option value="">--Selecione--</option>
            <option v-for="asset in imageAssets" :key="asset.slug" :value="asset.slug">{{ asset.label }}</option>
          </select>
        </div>
        <div class="col-md-6" v-if="options.background_source === 'file'">
          <label class="form-label">Imagem de fundo</label>
          <input
              type="file"
              class="form-control form-control-solid"
              accept="image/png,image/jpeg,image/jpg"
              :disabled="uploadingBackground"
              @change="uploadBackgroundImage"
          >
          <small class="text-muted d-block mt-2" v-if="uploadingBackground">Enviando imagem...</small>
          <small class="text-muted d-block mt-2" v-else-if="options.id_file">
            Arquivo #{{ options.id_file }}<span v-if="backgroundFileName"> — {{ backgroundFileName }}</span>
          </small>
        </div>
        <div class="col-md-12 mt-4" v-if="backgroundPreviewSrc">
          <label class="form-label">Pré-visualização</label>
          <img :src="backgroundPreviewSrc" alt="Pré-visualização da imagem de fundo" class="background-preview">
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="type === 'odontogram'" class="card mt-4">
    <hr>
    <h5 class="card-title mt-4">Configuração do odontograma</h5>
    <div class="card-body">
      <div class="row">
        <div class="col-md-6">
          <label class="form-label">Dentição</label>
          <select class="form-select form-control-solid" v-model="options.dentition" @change="emitOptions">
            <option value="permanent">Permanente (32 dentes)</option>
            <option value="deciduous">Decídua (20 dentes)</option>
          </select>
          <small class="text-muted d-block mt-2">Notação FDI. O profissional marca condição por dente e por face.</small>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="type === 'structured_list'" class="card mt-4">
    <hr>
    <h5 class="card-title mt-4">Configuração da lista</h5>
    <div class="card-body">
      <div class="row mb-3">
        <div class="col-md-12">
          <label class="form-label">Texto do botão adicionar</label>
          <input class="form-control form-control-solid" v-model="options.add_label" @input="emitOptions">
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label mb-0">Campos</label>
        <button type="button" class="btn btn-sm btn-light-primary" @click="addStructuredField">Adicionar campo</button>
      </div>

      <div class="card p-3 mb-3" v-for="(field, index) in options.fields" :key="`field-${index}`">
        <div class="row">
          <div class="col-md-3">
            <input class="form-control form-control-solid" placeholder="Key" v-model="field.key" @input="emitOptions">
          </div>
          <div class="col-md-4">
            <input class="form-control form-control-solid" placeholder="Label" v-model="field.label" @input="emitOptions">
          </div>
          <div class="col-md-3">
            <select class="form-select form-control-solid" v-model="field.type" @change="emitOptions">
              <option v-for="option in structuredFieldTypes" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="col-md-2">
            <button type="button" class="btn btn-sm btn-light-danger w-100" @click="removeStructuredField(index)">Remover</button>
          </div>
        </div>
        <div class="row mt-2 align-items-center">
          <div class="col-md-4">
            <label class="form-label fs-8 text-muted mb-1">Largura no formulário</label>
            <select class="form-select form-select-sm form-control-solid" v-model="field.col" @change="emitOptions">
              <option :value="undefined">Automática (pelo tipo)</option>
              <option :value="3">1/4 da linha</option>
              <option :value="4">1/3 da linha</option>
              <option :value="6">Metade da linha</option>
              <option :value="12">Linha inteira</option>
            </select>
          </div>
          <div class="col-md-4 pt-4">
            <label class="form-check form-check-custom form-check-sm">
              <input class="form-check-input" type="checkbox" v-model="field.required" @change="emitOptions">
              <span class="form-check-label">Obrigatório</span>
            </label>
          </div>
          <div class="col-md-4" v-if="field.type === 'textarea'">
            <label class="form-label fs-8 text-muted mb-1">Linhas</label>
            <input class="form-control form-control-sm form-control-solid" type="number" min="2" max="8"
                   v-model.number="field.rows" @input="emitOptions">
          </div>
        </div>
        <div class="row mt-2" v-if="field.type === 'select'">
          <div class="col-md-12">
            <label class="form-label">Opções</label>
            <DelimiterTagInput
                :model-value="field.options ?? []"
                @update:modelValue="updateSelectOptions(field, $event)"
            />
            <small class="text-muted">Separe com vírgula, ponto e vírgula ou Enter.</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../../helpers/http";
import Alert from "../../../helpers/alert";
import DelimiterTagInput from "./DelimiterTagInput.vue";

// Mesma lista aceita pela API (StructuredListFieldType) e desenhada pelo
// formulário do prontuário.
const STRUCTURED_FIELD_TYPES = [
  {value: 'text', label: 'Texto'},
  {value: 'textarea', label: 'Texto longo'},
  {value: 'select', label: 'Lista de opções'},
  {value: 'number', label: 'Número'},
  {value: 'time', label: 'Hora'},
  {value: 'date', label: 'Data'},
];

export default {
  name: "AttributeOptionsEditor",
  components: {DelimiterTagInput},
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: [Array, Object, String, null],
      default: null,
    },
    type: {
      type: String,
      default: '',
    },
    gridPresets: {
      type: Object,
      default: () => ({}),
    },
    imageAssets: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selectedPresetDescription() {
      if (!this.selectedPreset || !this.gridPresets[this.selectedPreset]) {
        return '';
      }

      const preset = this.gridPresets[this.selectedPreset];
      return typeof preset === 'object' ? (preset.description ?? '') : '';
    },
  },
  data() {
    return {
      structuredFieldTypes: STRUCTURED_FIELD_TYPES,
      selectedPreset: '',
      options: this.normalizeOptions(this.modelValue, this.type),
      uploadingBackground: false,
      backgroundFileName: '',
      backgroundPreviewSrc: '',
      backgroundObjectUrl: '',
    };
  },
  watch: {
    type(newType) {
      this.options = this.normalizeOptions(this.options, newType);
      this.emitOptions();
    },
    modelValue: {
      deep: true,
      handler(newValue) {
        this.options = this.normalizeOptions(newValue, this.type);
        this.syncBackgroundPreview();
      },
    },
  },
  mounted() {
    this.syncBackgroundPreview();
    this.emitOptions();
  },
  beforeUnmount() {
    this.revokeBackgroundObjectUrl();
  },
  methods: {
    normalizeOptions(value, type) {
      let parsed = value;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (_) {
          parsed = null;
        }
      }

      if (!parsed || typeof parsed !== 'object') {
        parsed = {};
      }

      if (type === 'grid') {
        return {
          columns: Array.isArray(parsed.columns) ? parsed.columns : [{key: 'od', label: 'OD'}, {key: 'os', label: 'OE'}],
          rows: Array.isArray(parsed.rows) ? parsed.rows : [],
          defaults: parsed.defaults && typeof parsed.defaults === 'object' ? parsed.defaults : {},
        };
      }

      if (type === 'image_annotation') {
        return {
          background_source: parsed.background_source ?? 'catalog',
          image_asset: parsed.image_asset ?? '',
          id_file: parsed.id_file ?? null,
        };
      }

      if (type === 'structured_list') {
        return {
          add_label: parsed.add_label ?? 'Adicionar item',
          fields: Array.isArray(parsed.fields) ? parsed.fields : [],
          defaults: Array.isArray(parsed.defaults) ? parsed.defaults : [],
        };
      }

      if (type === 'odontogram') {
        return {
          // A validacao do backend exige dentition: sem o default o atributo
          // nasceria invalido ao trocar o tipo na tela de configuracao.
          dentition: ['permanent', 'deciduous'].includes(parsed.dentition) ? parsed.dentition : 'permanent',
        };
      }

      return parsed;
    },
    emitOptions() {
      this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.options)));
    },
    onBackgroundSourceChange() {
      if (this.options.background_source === 'catalog') {
        this.options.id_file = null;
        this.backgroundFileName = '';
        this.setBackgroundPreviewFromThumbnail(null);
      }

      this.emitOptions();
      this.syncBackgroundPreview();
    },
    applyPreset() {
      const preset = this.gridPresets[this.selectedPreset];
      if (!preset) {
        return;
      }

      const config = preset.options ?? preset;
      this.options = JSON.parse(JSON.stringify(config));
      this.emitOptions();
    },
    presetLabel(preset) {
      if (preset && typeof preset === 'object' && preset.label) {
        return preset.label;
      }

      return preset;
    },
    addGridColumn() {
      this.options.columns.push({key: '', label: ''});
      this.emitOptions();
    },
    removeGridColumn(index) {
      this.options.columns.splice(index, 1);
      this.emitOptions();
    },
    addGridRow() {
      this.options.rows.push({key: '', label: '', cell_type: 'text', suggestions: []});
      this.emitOptions();
    },
    removeGridRow(index) {
      this.options.rows.splice(index, 1);
      this.emitOptions();
    },
    updateRowSuggestions(row, values) {
      row.suggestions = Array.isArray(values) ? values : [];
      this.emitOptions();
    },
    updateDefaultValue(rowKey, columnKey, value) {
      if (!this.options.defaults[rowKey]) {
        this.options.defaults[rowKey] = {};
      }

      this.options.defaults[rowKey][columnKey] = value;
      this.emitOptions();
    },
    addStructuredField() {
      this.options.fields.push({key: '', label: '', type: 'text', options: []});
      this.emitOptions();
    },
    removeStructuredField(index) {
      this.options.fields.splice(index, 1);
      this.emitOptions();
    },
    updateSelectOptions(field, values) {
      field.options = Array.isArray(values) ? values : [];
      this.emitOptions();
    },
    uploadBackgroundImage(event) {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      this.uploadingBackground = true;

      http.post('/record-types/background-images/upload', formData).then((response) => {
        this.options.id_file = response.data.id_file;
        this.backgroundFileName = response.data.file_name ?? file.name;
        this.setBackgroundPreviewFromThumbnail(response.data.file_thumbnail);
        this.emitOptions();
        Alert.toastSuccess('Imagem enviada com sucesso.');
      }).catch(() => {
        Alert.toastError('Falha ao enviar imagem.');
      }).finally(() => {
        this.uploadingBackground = false;
        event.target.value = '';
      });
    },
    setBackgroundPreviewFromThumbnail(thumbnail) {
      this.revokeBackgroundObjectUrl();
      this.backgroundPreviewSrc = thumbnail ? `data:image/jpeg;base64,${thumbnail}` : '';
    },
    async syncBackgroundPreview() {
      if (this.type !== 'image_annotation' || this.options.background_source !== 'file' || !this.options.id_file) {
        this.backgroundFileName = '';
        this.setBackgroundPreviewFromThumbnail(null);
        return;
      }

      try {
        const response = await http.get(`/record-types/files/${this.options.id_file}`, {responseType: 'blob'});
        this.revokeBackgroundObjectUrl();
        this.backgroundObjectUrl = URL.createObjectURL(response.data);
        this.backgroundPreviewSrc = this.backgroundObjectUrl;
      } catch (_) {
        this.setBackgroundPreviewFromThumbnail(null);
      }
    },
    revokeBackgroundObjectUrl() {
      if (this.backgroundObjectUrl) {
        URL.revokeObjectURL(this.backgroundObjectUrl);
        this.backgroundObjectUrl = '';
      }
    },
  },
};
</script>

<style scoped>
.background-preview {
  max-width: 320px;
  max-height: 180px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
}

.grid-rows-list {
  margin-top: 0.5rem;
}

.grid-row-divider {
  margin: 1.5rem 0;
  border: 0;
  border-top: 1px dashed #d8d8e5;
  opacity: 1;
}

.grid-row-block {
  padding: 1rem 1.25rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.75rem;
  background-color: #f9f9fb;
}

.grid-row-block__header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e4e6ef;
}

.grid-row-block__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #5e6278;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
</style>
